import { Canvas, useThree } from "@react-three/fiber";
import ParticleTree from "./models/TreeModel";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import React, { useEffect, useMemo, useRef, useState, Suspense, useImperativeHandle, forwardRef } from "react";
import CircularSpriteCloud from "./models/SurroundingPicture";
import { useCubeTexture } from "@react-three/drei";

// 使用 forwardRef 包装组件
const Member3D = forwardRef((props, ref) => {
  const { camera, scene, gl, size } = useThree();
  const skyboxTextures = useCubeTexture(
    [
      "stars_east.png",
      "stars_west.png",
      "stars_top.png",
      "stars_bottom.png",
      "stars_north.png",
      "stars_south.png",
    ],
    { path: "/textures/sky/" },
  );
  
  const controlsRef = useRef();
  const circlePictureCloudRef = useRef();

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    // 图片云相关方法
    focusOnImage: (id, options) => {
      if (circlePictureCloudRef.current) {
        return circlePictureCloudRef.current.focusOnImage(id, options);
      }
      console.warn("CircularSpriteCloud ref not available");
      return false;
    },
    
    getSelectedImageId: () => {
      if (circlePictureCloudRef.current) {
        return circlePictureCloudRef.current.getSelectedImageId();
      }
      return null;
    },
    
    getImageList: () => {
      if (circlePictureCloudRef.current) {
        return circlePictureCloudRef.current.getImageList();
      }
      return [];
    },
    
    resetCamera: () => {
      if (circlePictureCloudRef.current) {
        circlePictureCloudRef.current.resetCamera();
      }
    },
    
    setZoom: (zoomLevel) => {
      if (circlePictureCloudRef.current) {
        return circlePictureCloudRef.current.setZoom(zoomLevel);
      }
      return false;
    },
    
    getCameraState: () => {
      if (circlePictureCloudRef.current) {
        return circlePictureCloudRef.current.getCameraState();
      }
      return null;
    },
    
    // 相机控制相关方法
    resetOrbitCamera: () => {
      if (controlsRef.current) {
        // 重置相机到初始位置
        camera.position.set(
          1.9283489962170814,
          2.267174718925302,
          11.369567355846257,
        );
        
        const targetQuaternion = new THREE.Quaternion();
        targetQuaternion.setFromEuler(
          new THREE.Euler(
            0.12270485848529518,
            0.11340801158564079,
            -0.01395511126395539,
            "XYZ",
          ),
        );
        camera.quaternion.copy(targetQuaternion);
        camera.updateMatrixWorld();
        
        controlsRef.current.target.set(0, 3, 0);
        controlsRef.current.update();
      }
    },
    
    // 获取当前相机信息
    getCameraInfo: () => ({
      position: camera.position.toArray(),
      rotation: camera.rotation.toArray(),
      quaternion: camera.quaternion.toArray(),
    }),
    
    // 启用/禁用轨道控制
    enableOrbitControls: (enabled) => {
      if (controlsRef.current) {
        controlsRef.current.enabled = enabled;
      }
    }
  }));

  // 只在组件挂载时设置一次相机
  useEffect(() => {
    // 设置相机位置
    camera.position.set(
      1.9283489962170814,
      2.267174718925302,
      11.369567355846257,
    );

    // 设置相机朝向
    const targetQuaternion = new THREE.Quaternion();
    targetQuaternion.setFromEuler(
      new THREE.Euler(
        0.12270485848529518,
        0.11340801158564079,
        -0.01395511126395539,
        "XYZ",
      ),
    );
    camera.quaternion.copy(targetQuaternion);
    camera.rotation.set(
      0.12270485848529518,
      0.11340801158564079,
      -0.01395511126395539,
    );

    // 更新相机矩阵
    camera.updateMatrixWorld();

    // 设置OrbitControls的目标点
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 3, 0);
      controlsRef.current.update();
    }
  }, []); // 空依赖数组确保只运行一次

  const handleImageClick = (id, position) => {
    console.log(`Image ${id} clicked at`, position);
    // 可以在这里处理相机动画或状态更新
  };

  return (
    <>
      {/* Spherical skybox */}
      <mesh>
        <boxGeometry args={[1000, 1000, 1000]} />
        <meshBasicMaterial side={THREE.BackSide} envMap={skyboxTextures} />
      </mesh>

      <ParticleTree
        particleImageUrl={process.env.PUBLIC_URL + "/gradient.png"}
        objModelUrl={process.env.PUBLIC_URL + "/e.obj"}
        modelScale={5}
      />

      <Suspense fallback={null}>
        <CircularSpriteCloud
          atlasTextureUrl={props.atlasTextureUrl}
          atlasJsonUrl={props.atlasJsonUrl}
          radiusRange={[7, 10]}
          yRange={[0, 5]}
          rotationSpeed={-2}
          imageScale={0.1}
          maxImages={150}
          showTextOnHover={true}
          ref={circlePictureCloudRef}
        />
      </Suspense>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <pointLight position={[-5, 5, -5]} />
      <OrbitControls
        ref={controlsRef}
        enableZoom={true}
        enablePan={false}
        maxDistance={20}
      />
    </>
  );
});

export default Member3D;